import { useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { NewSidebar } from './NewSidebar';
import { NewHeader } from './NewHeader';
import { cn } from '../../lib/utils';
import { useSidebar } from '../../contexts/SidebarContext';
import { ChevronRight, Home } from 'lucide-react';
import EmailVerificationBanner from '../auth/EmailVerificationBanner';
import TrialBanner from '../subscription/TrialBanner';
import SubscriptionExpiredBanner from '../subscription/SubscriptionExpiredBanner';

const ROUTE_LABELS = {
  'dashboard': 'Dashboard',
  'payables': 'Contas a Pagar',
  'receivables': 'Contas a Receber',
  'extract': 'Extrato Financeiro',
  'reconciliation': 'Conciliação',
  'cash-flow': 'Fluxo de Caixa',
  'credit-cards': 'Cartões de Crédito',
  'contracts': 'Contratos',
  'sales': 'Vendas',
  'purchases': 'Compras',
  'invoices': 'Notas Fiscais',
  'entities': 'Entidades',
  'people': 'Pessoas',
  'products': 'Produtos',
  'services': 'Serviços',
  'categories': 'Categorias',
  'cost-centers': 'Centros de Custo',
  'accounts': 'Contas Bancárias',
  'automation': 'Automação',
  'automations': 'Automações',
  'budget': 'Orçamentos',
  'reports': 'Relatórios',
  'dre': 'DRE',
  'aging': 'Aging',
  'integrations': 'Integrações',
  'open-finance': 'Open Finance',
  'settings': 'Configurações',
  'profile': 'Perfil',
  'developer': 'Desenvolvedor',
  'reconciliation-rules': 'Regras de Conciliação',
  'new': 'Novo',
  'edit': 'Editar',
};

const ROUTE_GROUPS = {
  'payables': 'Financeiro',
  'receivables': 'Financeiro',
  'extract': 'Financeiro',
  'reconciliation': 'Financeiro',
  'cash-flow': 'Financeiro',
  'credit-cards': 'Financeiro',
  'contracts': 'Operacional',
  'sales': 'Comercial',
  'purchases': 'Comercial',
  'invoices': 'Comercial',
  'entities': 'Cadastros',
  'people': 'Cadastros',
  'products': 'Cadastros',
  'services': 'Cadastros',
  'categories': 'Cadastros',
  'cost-centers': 'Cadastros',
  'accounts': 'Cadastros',
  'automation': 'Sistema',
  'automations': 'Sistema',
  'budget': 'Financeiro',
  'integrations': 'Sistema',
  'open-finance': 'Sistema',
  'reconciliation-rules': 'Financeiro',
  'reports': 'Relatórios',
  'dre': 'Relatórios',
  'aging': 'Relatórios',
};

function Breadcrumb() {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  if (pathParts.length === 0 || (pathParts.length === 1 && pathParts[0] === 'dashboard')) return null;
  
  const firstPart = pathParts[0];
  const group = ROUTE_GROUPS[firstPart];
  
  return (
    /* Escondido no celular: a trilha inteira nao cabe em 360px e quebra em
       duas linhas, roubando espaco de quem so quer ver os lancamentos.
       O botao de voltar do proprio Android ja resolve a navegacao. */
    <nav className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-4" data-testid="breadcrumb">
      <Link to="/dashboard" className="hover:text-foreground transition-colors">
        <Home className="h-3.5 w-3.5" />
      </Link>
      {group && (
        <>
          <ChevronRight className="h-3 w-3" />
          <span className="text-muted-foreground">{group}</span>
        </>
      )}
      {pathParts.map((part, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <ChevronRight className="h-3 w-3" />
          {i < pathParts.length - 1 ? (
            <Link to={`/${pathParts.slice(0, i + 1).join('/')}`} className="hover:text-foreground transition-colors">
              {ROUTE_LABELS[part] || part}
            </Link>
          ) : (
            <span className="text-foreground font-medium">{ROUTE_LABELS[part] || part}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// Dynamic document title based on current route
function useDocumentTitle() {
  const location = useLocation();
  
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length === 0 || (pathParts.length === 1 && pathParts[0] === 'dashboard')) {
      document.title = 'OFS | Dashboard';
      return;
    }
    // Use the last meaningful part for the title
    const lastPart = pathParts[pathParts.length - 1];
    const label = ROUTE_LABELS[lastPart];
    if (label) {
      document.title = `OFS | ${label}`;
    } else {
      document.title = 'OFS';
    }
  }, [location.pathname]);
}

export function NewLayout() {
  const { collapsed } = useSidebar();
  useDocumentTitle();
  
  return (
    <div className="min-h-screen bg-[#F7F9FC] dark:bg-[#09090b]">
      {/* Sidebar - Fixed */}
      <NewSidebar />
      
      {/* Main Content Area
          O padding lateral so existe a partir de md: no celular a sidebar e'
          uma gaveta sobreposta, entao reservar 240px ali espremia o conteudo
          em ~120px de tela util. Era a causa do "layout de PC no celular". */}
      <div className={cn(
        "transition-all duration-300 ease-in-out min-h-screen flex flex-col",
        collapsed ? "md:pl-[72px]" : "md:pl-60"
      )}>
        {/* Header - Sticky */}
        <NewHeader />

        {/* Subscription expired (alta prioridade) */}
        <SubscriptionExpiredBanner />

        {/* Email verification banner (only if user.email_verified === false) */}
        <EmailVerificationBanner />

        {/* Trial countdown (apenas durante trial ativo) */}
        <TrialBanner />

        {/* Content - Scrollable.
            p-4 no celular: 24px de cada lado comiam 13% de uma tela de 360px. */}
        <main className="flex-1 p-4 md:p-6">
          <Breadcrumb />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Re-export existing components for backward compatibility
export { PageHeader, PageContent, StatsCard } from './Layout';
